import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { Kicker, Reveal, SectionTitle, staggerContainer, staggerItem } from "./Reveal";
import { BrandIcon } from "./BrandIcon";

export default function CommunitySection() {
  const c = useContent();
  const groups = c.pairs("community", "groups");
  const [active, setActive] = useState(0);
  const current = groups[active] || [];

  return (
    <section className="bg-surface" data-testid="community-section">
      <div className="mx-auto max-w-[1200px] px-5 py-20 md:py-28">
        <Reveal className="grid gap-6 md:grid-cols-12 md:items-end">
          <div className="md:col-span-7">
            <Kicker>{c.get("community", "kicker")}</Kicker>
            <SectionTitle>{c.get("community", "title")}</SectionTitle>
          </div>
          <p className="leading-relaxed text-body md:col-span-4 md:col-start-9">{c.get("community", "subtitle")}</p>
        </Reveal>

        <div className="mt-12 grid gap-12 md:mt-16 md:grid-cols-12">
          <motion.ul
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="md:col-span-7"
            onMouseLeave={() => setActive(active)}
          >
            {groups.map(([title, desc, img, icon], i) => {
              const on = i === active;
              return (
                <motion.li
                  key={i}
                  variants={staggerItem}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  tabIndex={0}
                  className={`group relative grid cursor-pointer grid-cols-[auto_1fr_auto] items-start gap-5 border-t border-line py-6 transition-colors duration-500 last:border-b md:gap-7 md:py-7 ${
                    on ? "text-pine" : "text-ink"
                  }`}
                  data-testid={`community-group-${i}`}
                  data-cursor="hover"
                >
                  <span
                    className={`flex h-12 w-12 items-center justify-center rounded-md transition-colors duration-500 ${
                      on ? "bg-pine text-white" : "bg-bone text-pine"
                    }`}
                  >
                    <BrandIcon name={icon} className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-current md:text-2xl">{title}</h3>
                    <motion.p
                      initial={false}
                      animate={{ height: on ? "auto" : 0, opacity: on ? 1 : 0, marginTop: on ? 12 : 0 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="max-w-lg overflow-hidden leading-relaxed text-body"
                    >
                      {desc}
                    </motion.p>
                    <div className="mt-4 aspect-[16/9] overflow-hidden rounded-[10px] md:hidden">
                      <img src={img} alt={title} loading="lazy" className="h-full w-full object-cover" />
                    </div>
                  </div>
                  <span className="flex items-center gap-3 pt-2">
                    <span className="font-display text-sm font-semibold text-goldtext">0{i + 1}</span>
                    <ArrowUpRight
                      className={`h-5 w-5 transition-all duration-500 ${
                        on ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0"
                      }`}
                    />
                  </span>
                  <motion.span
                    className="absolute bottom-[-1px] left-0 h-[2px] bg-terra"
                    initial={false}
                    animate={{ width: on ? "100%" : "0%" }}
                    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                  />
                </motion.li>
              );
            })}
          </motion.ul>

          <div className="hidden md:col-span-5 md:block">
            <div
              className="sticky top-28 aspect-[4/5] overflow-hidden rounded-[10px] bg-bone shadow-[0_10px_30px_rgba(18,33,27,0.08)]"
              data-testid="community-image-panel"
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.img
                  key={active}
                  src={current[2] || "/images/texture.jpg"}
                  alt={current[0] || ""}
                  initial={{ opacity: 0, scale: 1.08, clipPath: "inset(0 0 100% 0)" }}
                  animate={{ opacity: 1, scale: 1, clipPath: "inset(0 0 0% 0)" }}
                  exit={{ opacity: 0, scale: 1.04 }}
                  transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
                  className="img-editorial absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-ink/80 to-transparent p-6 text-sand">
                <span className="text-[11px] font-semibold uppercase tracking-[0.3em]">{current[0]}</span>
                <img src="/brand/leaf.png" alt="" className="h-8 w-8 object-contain" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
